import { useState, useEffect, useCallback, useMemo } from 'react';
import { ApiResponse, TripMetadata, Trip } from '../types';
import { TripPlannerAPI } from '../services/api';

// Generic hook for managing API state
export function useApi<T>(
  apiCall: () => Promise<T>,
  dependencies: any[] = []
): ApiResponse<T> & { refetch: () => void } {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(undefined);
      const result = await apiCall();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchData();
  }, dependencies);

  return { data, error, loading, refetch: fetchData };
}

// Hook for managing trip metadata (for listing)
export function useTripMetadata(userId: string | null) {
  const [trips, setTrips] = useState<TripMetadata[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);

  const fetchTrips = useCallback(async () => {
    if (!userId) {
      console.log('🔍 useTripMetadata: No userId provided, skipping fetch');
      setTrips([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(undefined);
      console.log(`🔍 Fetching trip metadata for user: ${userId}`);
      const response = await TripPlannerAPI.getUserTripsMetadata(userId);
      console.log(`✅ Trip metadata fetched successfully:`, response);
      console.log(`📊 Performance: Loaded ${response.trips.length} trip metadata objects (lightweight)`);
      setTrips(response.trips);
    } catch (err) {
      console.error(`❌ Error fetching trip metadata for user ${userId}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to fetch trip metadata');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    console.log(`🔍 useTripMetadata useEffect: userId=${userId}, calling fetchTrips`);
    fetchTrips();
  }, [userId, fetchTrips]);

  return { trips, loading, error, refetch: fetchTrips };
}

// Hook for managing complete trip data (lazy loading)
export function useTrip(tripId: string, shouldFetch: boolean = true) {
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const fetchTrip = useCallback(async () => {
    if (!tripId || tripId.trim() === '') {
      setError('No trip ID provided');
      setHasFetched(true);
      return;
    }
    
    try {
      setLoading(true);
      setError(undefined);
      console.log(`🔍 Fetching trip: ${tripId}`);
      const response = await TripPlannerAPI.getTrip(tripId);
      console.log(`✅ Trip fetched successfully:`, response);
      setTrip(response);
      setHasFetched(true);
    } catch (err) {
      console.error(`❌ Error fetching trip ${tripId}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to fetch trip');
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  // Only fetch when tripId changes, we haven't fetched yet, AND shouldFetch is true (i.e., user is authenticated)
  useEffect(() => {
    if (tripId && !hasFetched && shouldFetch) {
      fetchTrip();
    }
  }, [tripId, hasFetched, shouldFetch, fetchTrip]);

  return { trip, loading, error, refetch: fetchTrip };
}

// Hook for managing trip with lazy loading and caching
export function useTripWithCache(tripId: string, shouldFetch: boolean = true) {
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const fetchTrip = useCallback(async () => {
    if (!tripId || tripId.trim() === '') {
      setError('No trip ID provided');
      setHasFetched(true);
      return;
    }
    
    try {
      setLoading(true);
      setError(undefined);
      console.log(`🔍 Fetching cached trip: ${tripId}`);
      const response = await TripPlannerAPI.getTrip(tripId);
      console.log(`✅ Cached trip fetched successfully:`, response);
      setTrip(response);
      setHasFetched(true);
    } catch (err) {
      console.error(`❌ Error fetching cached trip ${tripId}:`, err);
      setError(err instanceof Error ? err.message : 'Failed to fetch trip');
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  // Only fetch when tripId changes, we haven't fetched yet, AND shouldFetch is true (i.e., user is authenticated)
  useEffect(() => {
    console.log('🔍 useTripWithCache useEffect:', { tripId, hasFetched, shouldFetch });
    if (tripId && !hasFetched && shouldFetch) {
      console.log('✅ useTripWithCache: Triggering fetchTrip');
      fetchTrip();
    } else {
      console.log('⏸️ useTripWithCache: Skipping fetch', { 
        noTripId: !tripId, 
        alreadyFetched: hasFetched, 
        shouldNotFetch: !shouldFetch 
      });
    }
  }, [tripId, hasFetched, shouldFetch, fetchTrip]);

  // Function to update trip state optimistically
  const updateTrip = useCallback((updater: (currentTrip: Trip | null) => Trip | null) => {
    setTrip(prevTrip => {
      const updatedTrip = updater(prevTrip);
      console.log(`🔄 Optimistic update applied:`, updatedTrip);
      return updatedTrip;
    });
  }, []);

  // Memoize the trip data to prevent unnecessary re-renders
  const memoizedTrip = useMemo(() => trip, [trip]);

  return { 
    trip: memoizedTrip, 
    loading, 
    error, 
    refetch: fetchTrip,
    updateTrip // New function for optimistic updates
  };
}

// Hook for managing form state
export function useFormState<T>(initialState: T) {
  const [formData, setFormData] = useState<T>(initialState);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});

  const updateField = useCallback((field: keyof T, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const setFieldError = useCallback((field: keyof T, error: string) => {
    setErrors(prev => ({ ...prev, [field]: error }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData(initialState);
    setErrors({});
  }, [initialState]);

  const validateForm = useCallback((validationRules: Record<keyof T, (value: any) => string | undefined>) => {
    const newErrors: Partial<Record<keyof T, string>> = {};
    let isValid = true;

    Object.keys(validationRules).forEach((key) => {
      const field = key as keyof T;
      const value = formData[field];
      const error = validationRules[field](value);
      
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [formData]);

  return {
    formData,
    errors,
    updateField,
    setFieldError,
    resetForm,
    validateForm,
  };
}

// Hook for managing local storage
export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue] as const;
}

// Hook for managing window size
export function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to set initial size

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}
